import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { authApi } from '../../../api/auth';
import { API_BASE } from '../../../api/fetchHelper';

type EnrollmentRow = {
    id: string;
    name: string;
    email: string;
    grade?: number;
    subject?: string;
    enrolledAt?: string;
};

type StudentRow = {
    id: string;
    name: string;
    email: string;
    subjects: string[];
    firstEnrolledAt?: string;
};

export function TeacherStudentsTab({ teacherId }: { teacherId: string }) {
    const { data, isLoading } = useQuery({
        queryKey: ['teacher-students-admin', teacherId],
        queryFn: async (): Promise<EnrollmentRow[]> => {
            const res = await fetch(`${API_BASE}/teacher/students?teacherId=${teacherId}`, {
                headers: { 'Authorization': `Bearer ${authApi.getToken()}` },
            });
            const json = await res.json();
            return (json.data?.students || []) as EnrollmentRow[];
        },
    });

    // Dedupe: backend returns one entry per (student × subject) enrollment.
    // Show one row per student with subjects collected and earliest enrolledAt.
    const students: StudentRow[] = (() => {
        const byId = new Map<string, StudentRow>();
        for (const s of data || []) {
            const existing = byId.get(s.id);
            if (existing) {
                if (s.subject && !existing.subjects.includes(s.subject)) existing.subjects.push(s.subject);
                if (s.enrolledAt && (!existing.firstEnrolledAt || s.enrolledAt < existing.firstEnrolledAt)) {
                    existing.firstEnrolledAt = s.enrolledAt;
                }
            } else {
                byId.set(s.id, {
                    id: s.id,
                    name: s.name,
                    email: s.email,
                    subjects: s.subject ? [s.subject] : [],
                    firstEnrolledAt: s.enrolledAt,
                });
            }
        }
        return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
    })();

    if (isLoading) return <div style={{ padding: 16, color: 'var(--text-tertiary)' }}>Loading...</div>;
    if (students.length === 0) return <div style={{ padding: 16, color: 'var(--text-tertiary)' }}>No students enrolled with this teacher.</div>;

    return (
        <div className="dt-container--bordered" style={{ marginTop: 12 }}>
            <table className="dt dt--compact">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Subjects</th>
                        <th>Enrolled</th>
                    </tr>
                </thead>
                <tbody>
                    {students.map((s) => (
                        <tr key={s.id}>
                            <td>{s.name}</td>
                            <td>{s.email}</td>
                            <td>{s.subjects.length ? s.subjects.join(', ') : '-'}</td>
                            <td>{s.firstEnrolledAt ? format(new Date(s.firstEnrolledAt), 'MMM d, yyyy') : '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
